import React from "react";
import { createSelector } from "reselect";
import flattenObject from "flat";
import flatten from "lodash/flatten";
import numeral from "numeral";

numeral.register("locale", "de", {
  delimiters: {
    thousands: ".",
    decimal: ","
  },
  abbreviations: {
    thousand: "k",
    million: "m",
    billion: "b",
    trillion: "t"
  },
  ordinal: function (number) {
    return ".";
  },
  currency: {
    symbol: "€"
  }
});

numeral.locale("de");

export const doubleDigits = (num) => numeral(num).format("0,0.00");

export const selectIsAuthenticated = (state) =>
  state.accessToken && state.tokenType;

export const selectError = (state) => state.error;

export const selectIsUploading = (state) => state.isUploading;
export const selectIsProcessing = (state) => state.isProcessing;
export const selectExtractionsError = (state) => state.extractionsError;
export const selectDocumentId = (state) => state.documentId;
export const selectCurrentPageIndex = (state) => state.currentPage;
export const selectImagesLoaded = (state) => state.imagesLoaded;

export const selectPages = (state) => state.pages;

export const selectNumberOfPages = createSelector(selectPages, (pages) =>
  pages ? Object.keys(pages).length : 0
);

export const selectCurrentPage = (state) => {
  return state.currentPage !== null && state.pages
    ? state.pages[state.currentPage].data
    : "";
};

export const selectExtractions = (state) => state.extractions;
export const selectBoundingBoxes = (state) => state.bboxes;
export const selectActiveBoxes = (state) => state.activeBoxes;
export const selectLines = (state) => state.lines;

export const selectHasExtractions = createSelector(
  selectExtractions,
  (extractions) => !!extractions
);

const withValue = ({ value }) => value;

const mapBoxes = (currentPageIndex) => (item) => ({
  ...item,
  confidence: numeral(item.confidence).format("0.[00]%"),
  bbox_ids: item.bbox_refs
    .filter((ref) => ref.page_num === currentPageIndex + 1)
    .map((ref) => ref.bbox_id)
});

const toCurrency = (currency) => (i) => ({
  ...i,
  value:
    typeof i.value === "number" && i.label !== "Quantity"
      ? `${doubleDigits(i.value)} ${currency}`
      : i.value
});

export const selectCurrency = createSelector(
  selectExtractions,
  (extractions) => extractions?.currency?.value || ""
);

export const selectGeneralExtractions = createSelector(
  selectExtractions,
  selectCurrentPageIndex,
  (extractions, currentPageIndex) => {
    if (!extractions) {
      return [];
    }

    return [
      { ...extractions.date, label: "Date" },
      { ...extractions.number, label: "Invoice Number" },
      { ...extractions.delivery_note_number, label: "Delivery Note Number" },
      { ...extractions.order_number, label: "Order Number" },
      {
        ...extractions.order_confirmation_number,
        label: "Order Confirmation Number"
      },
      { ...extractions.service_period, label: "Service Period" }
    ]
      .filter(withValue)
      .map(mapBoxes(currentPageIndex));
  }
);

export const selectCustomerExtractions = createSelector(
  selectExtractions,
  selectCurrentPageIndex,
  (extractions, currentPageIndex) => {
    if (!extractions) {
      return [];
    }

    const customer = extractions?.customer;

    return [
      { ...customer?.name, label: "Name" },
      { ...customer?.address, label: "Address" },
      { ...customer?.vat_id, label: "VAT ID" },
      { ...customer?.tax_number, label: "Tax Number" },
      ...mapBankingInformation(customer.banking_information),
      { ...customer?.customer_number, label: "Customer Number" },
      { ...customer?.eori_number, label: "EORI number" }
    ]
      .filter(withValue)
      .map(mapBoxes(currentPageIndex));
  }
);

export const selectLineItemExtractions = createSelector(
  selectExtractions,
  selectCurrentPageIndex,
  selectCurrency,
  (extractions, currentPageIndex, currency) => {
    if (!extractions) {
      return [];
    }

    const lineItems = extractions?.line_item;

    const mapped = [];
    lineItems.forEach((item) => {
      mapped.push({
        props: [
          { ...item?.discount, label: "Discount" },
          { ...item?.quantity, label: "Quantity" },
          { ...item?.tax_code, label: "Tax code" },
          { ...item?.tax_rate, label: "Tax rate" },
          { ...item?.article_id, label: "Article ID" },
          { ...item?.unit_price, label: "Unit price" },
          { ...item?.description, label: "Description" },
          { ...item?.total_price, label: "Total price" }
        ]
          .filter(withValue)
          .map(toCurrency(currency))
          .map(mapBoxes(currentPageIndex))
      });
    });

    return mapped;
  }
);

const mapTaxCalculations = (currency) => (calculations) => {
  if (!calculations || calculations.length === 0) {
    return [];
  }

  const taxCalculations = [];
  calculations.forEach((calc, index) => {
    const confidence = () => {
      const taxRateConfidence = calc?.tax_rate?.confidence;
      const netAmountConfidence = calc?.net_amount?.confidence;
      const taxAmountConfidence = calc?.tax_amount?.confidence;

      if (taxRateConfidence || netAmountConfidence || taxAmountConfidence) {
        return (
          (taxRateConfidence + netAmountConfidence + taxAmountConfidence) / 3
        );
      }

      return 0;
    };

    taxCalculations.push({
      label: `Tax Calculation ${index + 1}`,
      value: `${calc?.tax_rate?.value}% on ${doubleDigits(
        calc?.net_amount?.value
      )} = ${doubleDigits(calc?.tax_amount?.value)} ${currency}`,
      bbox_refs: flatten(
        Object.values(calc)
          .filter((item) => item?.bbox_refs?.length > 0)
          .map((item) => item?.bbox_refs)
      ),
      confidence: confidence()
    });
  });

  return taxCalculations;
};

const mapPaymentBenefits = (currency) => (benefits) => {
  if (!benefits || benefits.length === 0) {
    return [];
  }

  const mapped = [];
  benefits.forEach((benefit, index) => {
    const date = benefit?.early_payment_date?.value;
    const amount = benefit?.discount_amount?.value;
    const percentage = benefit?.discount_percentage?.value;

    if (date && amount && percentage) {
      mapped.push({
        label: `Early Payment Benefit ${index + 1}`,
        value: `${date}: ${doubleDigits(amount)} ${currency} = ${percentage}%`,
        bbox_refs: flatten(
          Object.values(benefit)
            .filter((item) => item?.bbox_refs?.length > 0)
            .map((item) => item?.bbox_refs)
        )
      });
    }
  });

  return mapped;
};

const mapBankingInformation = (bankingInformation) => {
  if (!bankingInformation || bankingInformation.length === 0) {
    return [];
  }

  const mapped = [];
  bankingInformation.forEach((banking, index) => {
    const ibanValue = banking?.iban?.value ? `IBAN: ${banking.iban.value}` : "";
    const bicValue = banking?.bic?.value ? `BIC: ${banking.bic.value}` : "";
    const value = `${ibanValue}\n${bicValue}`.split("\n").map((item, i) => (
      <p style={{ padding: 0, margin: 0 }} key={i}>
        {item}
      </p>
    ));

    const confidence = () => {
      const ibanConfidence = banking?.iban?.confidence;
      const bicConfidence = banking?.bic?.confidence;

      if (ibanConfidence && bicConfidence) {
        return (ibanConfidence + bicConfidence) / 2;
      }

      if (ibanConfidence && !bicConfidence) {
        return ibanConfidence;
      }

      if (bicConfidence && !ibanConfidence) {
        return bicConfidence;
      }

      return 0;
    };

    mapped.push({
      label: `Banking Information ${index + 1}`,
      value,
      bbox_refs: flatten(
        Object.values(banking)
          .filter((item) => item?.bbox_refs?.length > 0)
          .map((item) => item?.bbox_refs)
      ),
      confidence: confidence()
    });
  });

  return mapped;
};

export const selectPaymentExtractions = createSelector(
  selectExtractions,
  selectCurrentPageIndex,
  selectCurrency,
  (extractions, currentPageIndex, currency) => {
    if (!extractions) {
      return [];
    }

    return [
      { ...extractions.net_amount, label: "Total Net" },
      { ...extractions.gross_amount, label: "Total Gross" },
      { ...extractions.tax_amount, label: "Total Tax Amount" },
      ...mapTaxCalculations(currency)(extractions.tax_calculation),
      { ...extractions.due_date, label: "Due Date" },
      ...mapPaymentBenefits(currency)(extractions.early_payment_benefit),
      { ...extractions.payment_method, label: "Payment Method" }
    ]
      .filter(withValue)
      .map(toCurrency(currency))
      .map(mapBoxes(currentPageIndex));
  }
);

export const selectVendorExtractions = createSelector(
  selectExtractions,
  selectCurrentPageIndex,
  (extractions, currentPageIndex) => {
    if (!extractions) {
      return [];
    }

    const vendor = extractions?.vendor;

    return [
      { ...vendor.name, label: "Name" },
      { ...vendor.address, label: "Address" },
      { ...vendor.vat_id, label: "VAT ID" },
      { ...vendor.tax_number, label: "Tax Number" },
      ...mapBankingInformation(vendor.banking_information),
      { ...vendor.eori_number, label: "EORI number" },
      { ...vendor.register_id, label: "Register ID" },
      { ...vendor.phone, label: "Phone" },
      { ...vendor.fax, label: "Fax" },
      { ...vendor.url, label: "Website" },
      { ...vendor.e_mail, label: "Email" }
    ]
      .filter(withValue)
      .map(mapBoxes(currentPageIndex));
  }
);

export const selectAllBoundingBoxes = createSelector(
  selectBoundingBoxes,
  selectCurrentPageIndex,
  (boundingBoxes, currentPageIndex) => {
    if (!boundingBoxes?.byPage) {
      return [];
    }

    const boxes = boundingBoxes.byPage[currentPageIndex];
    return boxes;
  }
);

export const selectMatchingBoundingBoxes = createSelector(
  selectExtractions,
  selectBoundingBoxes,
  selectCurrentPageIndex,
  (extractions, boundingBoxes, currentPageIndex) => {
    // work in progress ;)
    if (!extractions || !boundingBoxes?.byPage) {
      return [];
    }

    const matchingIds = [];

    Object.values(extractions).forEach((extraction) => {
      if (extraction?.bbox_refs?.length > 0) {
        extraction.bbox_refs.forEach(({ bbox_id }) => {
          matchingIds.push(bbox_id);
        });
      }

      if (extraction !== null && !extraction?.value) {
        Object.values(extraction).forEach((nested) => {
          if (nested?.bbox_refs?.length > 0) {
            nested.bbox_refs.forEach(({ bbox_id }) => {
              matchingIds.push(bbox_id);
            });
          }
        });
      }

      extractions.line_item.forEach((lineItem) => {
        Object.values(lineItem).forEach((prop) => {
          if (prop?.bbox_refs?.length > 0) {
            prop.bbox_refs.forEach(({ bbox_id }) => {
              matchingIds.push(bbox_id);
            });
          }
        });
      });
    });

    const boxes = boundingBoxes.byPage[currentPageIndex];
    const filtered = boxes.filter(({ id }) => matchingIds.indexOf(id) > -1);

    return filtered;
  }
);

export const selectExtractionsWithValues = createSelector(
  selectExtractions,
  (extractions) => {
    if (!extractions) {
      return [];
    }

    const flattened = flattenObject(extractions);

    const filtered = Object.entries(flattened)
      .filter(([key, value]) => value !== null)
      .map(([key, value]) => {
        const formattedKey = key.replace(".value", "");
        return { key: formattedKey, value };
      });

    return filtered;
  }
);
